/**
 * Form for generating an api token.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm, SubmissionError } from 'redux-form';
import { withStyles } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';

import BusyButton from 'components/BusyButton/BusyButton';
import WrappedTextField from 'components/WrappedTextField/WrappedTextField';
import { maxLength, required, isAlphanumericUnderscore } from 'services/validationMsgs';

const maxLength16 = maxLength(16);

const propTypes = {
  classes: PropTypes.shape({
    field: PropTypes.string.isRequired,
    btn: PropTypes.string.isRequired,
  }).isRequired,
  error: PropTypes.string,
  className: PropTypes.string,
  handleSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  valid: PropTypes.bool.isRequired,
  pristine: PropTypes.bool.isRequired,
  onTokenCreate: PropTypes.func.isRequired,
};

const defaultProps = {
  className: null,
  error: null,
};

export function UnStyledApiTokenForm({
  classes,
  className,
  valid,
  error,
  pristine,
  submitting,
  handleSubmit,
  onTokenCreate,
}) {
  const handleFormSubmissionReturn = (result) => {
    if (result.payload && result.payload.name === 'RequestError') return;
    if (result.error) {
      throw new SubmissionError(result.formErrors);
    }
  };

  const submitHandler = (data) => onTokenCreate(data).then(handleFormSubmissionReturn);

  return (
    <form onSubmit={handleSubmit(submitHandler)} className={className}>
      <Field
        placeholder="Name"
        name="name"
        component={WrappedTextField}
        label="Name"
        variant="outlined"
        props={{ InputLabelProps: { shrink: true, className: classes.inputLabel } }}
        validate={[required, maxLength16, isAlphanumericUnderscore]}
        className={classes.field}
        fullWidth
        autoComplete="off"
      />
      {error && !submitting ? (
        <Typography color="error" className={classes.error}>
          {error}
        </Typography>
      ) : null}
      <BusyButton
        size="large"
        type="submit"
        fullWidth
        loading={submitting}
        disabled={!valid || pristine}
        variant="contained"
        color="primary"
        className={classes.btn}
      >
        Generate API Token
      </BusyButton>
    </form>
  );
}

UnStyledApiTokenForm.propTypes = propTypes;
UnStyledApiTokenForm.defaultProps = defaultProps;

const styles = (theme) => ({
  field: {
    marginTop: theme.spacing(2),
    '& label': {
      color: '#231f20 !important',
    },
  },
  btn: {
    marginTop: theme.spacing(2),
  },
  error: {
    marginTop: theme.spacing(2),
  },
  inputLabel: {
    color: theme.palette.text.primary,
  },
});

export default withStyles(styles)(
  reduxForm({
    form: 'apiTokenForm',
    destroyOnUnmount: true,
  })(UnStyledApiTokenForm),
);
